/**
 * [MC] Collection Redesign — productionised from the winning Convert variation
 * ("[MC] Collection Redesign - 3-Wick Candles", experience 1004211781, variation 1).
 *
 * The collection grid on goosecreek.com is rendered by the Prefixbox app (React). This does NOT replace
 * it — Prefixbox keeps owning the products, filtering, sorting and paging. What we do:
 *
 *  1. Scope class `mc-cr` (+ `mc-cr--v1`) on <html>; CSS hides the page furniture the design drops
 *     (top-category tile rows, AI image block, Trustpilot strip, spacer) and Prefixbox's own chrome
 *     (filter pills row, sort select, result count, tile/list toggle, numbered pager).
 *  2. Intro: rewrites the theme's collection header into the split layout — breadcrumb, collection title,
 *     first sentence of the description + "Read more", hero image on the right.
 *  3. Filter & Sort drawer: "Filter" and "Sort" open one drawer (Figma 639-5 states) that is a facade
 *     over Prefixbox's own state — options/counts are read from the app's filter components, edits are
 *     written to the URL (?pf-filters / ?pf-sort-direction) and applied by dispatching `popstate`, the
 *     exact path the app uses for back/forward. Edits apply live, so closing the drawer by any route
 *     (Show products / × / backdrop / Escape) keeps what was chosen; only "Clear all" resets. Applied
 *     filters (and the active sort) show as our own chips row under the toolbar (Figma 648-1565).
 *  4. Grid: Prefixbox cards stay as-is and are restyled by CSS; JS tags the containers, strips the
 *     redundant product-name suffix, and drops the promo tile in after the Nth card. All of this is
 *     re-applied by a MutationObserver every time Prefixbox re-renders. A filter or sort change keeps a
 *     dimmed snapshot of the previous cards over the grid until the new cards and their images are in.
 *  5. Paging facade: advances Prefixbox's page through its URL state (?pf-page + popstate — the theme's
 *     own pager buttons scroll the page to the results on click, so they are never used).
 *  6. About: wraps the bottom description section in a collapsed accordion row.
 *
 * Everything collection-specific (title, breadcrumb, hero, promo tile, name suffix) comes from the
 * JSON config the section prints as #mc-cr-config. With no config the built-in defaults reproduce the
 * original 3-Wick Candles test exactly.
 */
(function () {
  'use strict';
  if (document.documentElement.classList.contains('mc-cr')) return;

  var VARIANT = 1;

  // ── config from the section (sections/mc-collection-redesign.liquid) ─────
  var CFG = (function () {
    // Nothing here is collection artwork: an empty promo image means this collection has no tile, not
    // that it should borrow another collection's. Every value is taken verbatim from the config when the
    // key is present, so clearing e.g. the name suffix really does mean "strip nothing".
    var cfg = {
      about: '',
      stripSuffix: '',
      promoAfterCard: 8,
      paging: 'buttons',
      promo: { desktop: '', mobile: '', link: '', alt: '' }
    };
    try {
      var node = document.getElementById('mc-cr-config');
      if (!node) return cfg;
      var got = JSON.parse(node.textContent || '{}');
      Object.keys(got).forEach(function (k) {
        var v = got[k];
        if (v === null || v === undefined) return;
        if (k === 'promo') {
          Object.keys(v).forEach(function (kk) { if (v[kk] !== null && v[kk] !== undefined) cfg.promo[kk] = v[kk]; });
        } else {
          cfg[k] = v;
        }
      });
    } catch (e) { /* malformed config — the page still renders, just without the per-collection extras */ }
    return cfg;
  })();

  // ── products per page: every row full, at every width ────────────────────
  // The app asks its API for 24 per page (its own setting). The grid is 5 columns from 1440px, 4 from
  // 1024px and 2 below, and the promo tile takes 2 cells, so no single count fills the last row everywhere
  // (23 + 2 = 25 fills 5 columns but leaves 3 holes in 4 and 1 in 2). So the count follows the columns:
  // the first multiple of the column count from 24 cells, minus the promo's cells — 23 at 5 columns, 22 at
  // 4 and 2 (25 / 24 / 24 with no promo). Phones and tablets share a count, so rotating doesn't change it.
  // Its results request is a plain fetch to api.prefixbox.com carrying `top=24&page=N`; rewriting `top`
  // sets the count. The server pages by `top`, so page N is products (N−1)×size+1 … N×size, and the pager
  // below counts pages from the same size. Only that request is touched (it also carries `page`; the site
  // search suggestions don't). If the request ever changes shape the rewrite doesn't apply and the app
  // keeps 24 — the page maths notices an oversize page and falls back. Crossing a column breakpoint
  // re-asks for the current page at the new size (see the resize handler in init).
  //
  // NOTE vs. the Convert build: Convert injected this in <head>, ahead of Prefixbox. As a theme asset the
  // section loads it in the body, so the app's async module may already have fired its first request. That
  // is exactly the case requery() below was written for, so the outcome is the same — one extra round trip
  // on first paint at worst.
  var HAS_PROMO = !!(CFG.promo && CFG.promo.desktop);
  var gridCols = function () {   // the same steps as the grid's media queries in mc-collection-redesign.css
    return window.matchMedia('(min-width: 1440px)').matches ? 5 : window.matchMedia('(min-width: 1024px)').matches ? 4 : 2;
  };
  var pageSizeFor = function (cols) { return Math.ceil(24 / cols) * cols - (HAS_PROMO ? Math.min(2, cols) : 0); };
  var PAGE_SIZE = pageSizeFor(gridCols());
  (function () {
    var orig = window.fetch;
    if (typeof orig !== 'function') return;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (url && /\/\/api\.prefixbox\.com\//.test(url) && /[?&]page=\d+/.test(url) && /[?&]top=\d+/.test(url)) {
          var next = url.replace(/([?&]top=)\d+/, '$1' + PAGE_SIZE);
          input = typeof input === 'string' ? next : new Request(next, input);
        }
      } catch (e) { /* leave the request alone */ }
      return orig.call(this, input, init);
    };
  })();
  // The app's widget is an async module in the head, so its first request (and the next page it prefetches
  // with it) can go out before this script has run — those renders come back with 24 cards. Then ask again
  // with the rewrite in place: a filter expression that matches everything is a state change to the app (a
  // plain popstate is not), and the URL is put straight back once the app has read it, so neither the
  // address bar nor our chips ever show it.
  var requeries = 0, requeryAt = 0;
  // Which pages were fetched without the rewrite: every results request shows up in the browser's resource
  // log with its full URL, so any `top` other than ours marks that page stale (the count alone can't tell —
  // a stale last page comes back with fewer cards, not more). Buffered so a request that finished before
  // this script ran is seen too.
  var stalePages = {};
  (function () {
    if (typeof PerformanceObserver !== 'function') return;
    try {
      if (performance.setResourceTimingBufferSize) performance.setResourceTimingBufferSize(1000);
      var po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          var m = /\/\/api\.prefixbox\.com\/.*[?&]page=(\d+)(?:&|$)/.test(e.name) && e.name.match(/[?&]top=(\d+)/);
          if (m && m[1] !== String(PAGE_SIZE)) stalePages[e.name.match(/[?&]page=(\d+)/)[1]] = true;
        });
      });
      po.observe({ type: 'resource', buffered: true });
    } catch (e) { /* no observer — the card count check below still catches most cases */ }
  })();
  // The app keeps the match-everything filter in its state after the URL is put back, so asking with the
  // same one twice is no change and fetches nothing (a resize back across a column breakpoint is a second
  // ask). Alternate between two match-everything filters so every ask is a change; the first is as before.
  var MATCH_ALL = ['((price ge 0))', '((price le 99999999))'];
  function requery() {
    var url = new URL(location.href), clean = url.href;
    var inner = (url.searchParams.get('pf-filters') || '').trim().replace(/^\(/, '').replace(/\)$/, '');
    url.searchParams.set('pf-filters', '(' + (inner ? inner + ' and ' : '') + MATCH_ALL[(requeries + 1) % 2] + ')');
    history.replaceState({}, '', url.href);
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    history.replaceState({}, '', clean);
  }

  // Mid-collection promo tile (custom.mid_collection_image / _mobile). Blank desktop = no tile at all.
  var PROMO = {
    desktop: CFG.promo.desktop,
    mobile: CFG.promo.mobile || CFG.promo.desktop
  };
  var PROMO_AFTER_CARD = parseInt(CFG.promoAfterCard, 10) > 0 ? parseInt(CFG.promoAfterCard, 10) : 8;
  // Paging mode. 'buttons' = numbered page buttons styled like the chips, progress summary below (client
  // call, 2026-09-16). 'loadmore' = the frames' Load more + Show previous + remembered pages — kept intact
  // so flipping this one value brings it back.
  var PAGING = CFG.paging === 'loadmore' ? 'loadmore' : 'buttons';
  var MOBILE_MQ = '(max-width: 1023px)'; // Prefixbox's own lg breakpoint — filters live in the drawer below it
  var COPY = {
    readMore: 'Read more',
    readLess: 'Read less',
    filter: 'Filter',
    sort: 'Sort',
    clearAll: 'Clear all',
    sortBy: 'Sort by',
    emptyTitle: 'No products match these filters',
    emptyText: 'Try another scent or clear your filters.',
    emptyClear: 'Clear all filters',
    loadMore: 'Load more',
    loadPrev: 'Show previous products',
    pagePrev: 'Previous page',
    pageNext: 'Next page',
    pageN: function (n) { return 'Page ' + n; },
    viewed: function (n, m) { return "You've viewed " + n + ' of ' + m + ' products'; },
    about: CFG.about
  };
  var ICON_FILTER = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  var ICON_CHEVRON = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ── helpers ──────────────────────────────────────────────────────────────
  var pollerLite = function (conditions, callback, maxTime) {
    var start = Date.now();
    var t = setInterval(function () {
      var ok = conditions.every(function (c) { return typeof c === 'function' ? c() : !!document.querySelector(c); });
      if (ok) { clearInterval(t); callback(); }
      else if (Date.now() - start >= (maxTime || 15000)) { clearInterval(t); console.error('[mc-cr] polling exceeded maximum time limit'); }
    }, 25);
  };
  var qs = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var qsa = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var el = function (tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; };
  var section = function (fragment) { return qs('.collection-main--wrapper > .shopify-section[id*="' + fragment + '"]'); };
  var isMobile = function () { return window.matchMedia(MOBILE_MQ).matches; };

  // ── 1. intro ─────────────────────────────────────────────────────────────
  // The header is server-rendered by the "MC collection header" block on the Collection header section
  // (gated on custom.show_custom_collection_header), so there is nothing to rebuild here — only the
  // "Read more" fold, which needs the rendered copy in order to split it on its first sentence.
  // Collections without that block simply have no .mc-cr-intro-block and skip all of this.
  function initIntro() {
    var block = qs('.mc-cr-intro-block');
    if (!block || block.getAttribute('data-mc-intro') === '1') return;
    block.setAttribute('data-mc-intro', '1');
    var sec = block.closest('.shopify-section');
    if (sec) sec.classList.add('mc-cr-intro');

    var wrap = qs('.mc-cr-desc', block);
    if (!wrap) return;
    var ps = qsa('p', wrap);
    var first = ps[0] || wrap;
    var extras = ps.slice(1);                          // rich-text copy can be several paragraphs
    var text = first.textContent.replace(/\s+/g, ' ').trim();
    var m = text.match(/^(.+?[.!?])\s+(.+)$/);
    if (!m && !extras.length) return;                  // one sentence in one paragraph — nothing to fold

    var rest = null;
    if (m) {
      first.textContent = '';
      first.appendChild(document.createTextNode(m[1] + ' '));
      rest = el('span', 'mc-cr-desc__rest', '');
      rest.textContent = m[2];
      rest.hidden = true;
      first.appendChild(rest);
    }
    extras.forEach(function (n) { n.hidden = true; });

    var more = el('button', 'mc-cr-readmore', COPY.readMore);
    more.type = 'button';
    more.setAttribute('aria-expanded', 'false');
    more.addEventListener('click', function () {
      var open = rest ? rest.hidden : extras[0].hidden;
      if (rest) rest.hidden = !open;
      extras.forEach(function (n) { n.hidden = !open; });
      more.textContent = open ? COPY.readLess : COPY.readMore;
      more.setAttribute('aria-expanded', String(open));
    });
    // Inside the copy, not after it. The test put the button in the rich-text container immediately
    // following the paragraph, where it is in normal block flow and sits flush against the text. Placed
    // as a sibling of .mc-cr-desc it becomes a flex child of .title-bar and picks up that row-gap:20px,
    // which is the space above it that the test does not have. Appending puts it after any paragraphs
    // the fold reveals rather than between them.
    var host = ps.length ? first.parentNode : wrap;
    host.appendChild(more);
  }

  // ── 2. Filter & Sort drawer — a facade over Prefixbox's URL state ────────
  // Prefixbox mirrors its state into the URL (?pf-filters=…, ?pf-sort-direction=…, ?pf-page=N) and
  // re-reads it on `popstate` — the same path it uses for back/forward. Writing the URL and dispatching
  // popstate therefore applies every change in ONE request (a bare pushState does nothing — verified).
  // Option lists come from the filter components' own props (the app's virtualised dropdowns render
  // only on real pointer events, so they can't be scraped); the selected state is parsed from the URL.
  var GROUPS = [
    { key: 'custom_fragrance_type', label: 'Fragrance Category', hint: 'Fragrance category', multi: true },
    { key: 'brand', label: 'Fragrance Name', hint: 'All fragrances', multi: false, scroll: true },
    { key: 'productType', label: 'Fragrance Type', hint: 'All product types', multi: false }
  ];
  var PRICE = { label: 'Price', hint: 'Any price', buckets: [
    { id: '', label: 'Any price', expr: '' },
    { id: 'under15', label: 'Under $15', expr: '(price lt 15)' },
    { id: '15to25', label: '$15–$25', expr: '(price ge 15) and (price le 25)' },
    { id: '25up', label: '$25 & up', expr: '(price gt 25)' }
  ] };
  // Prefixbox's sort values; "Best selling" is not a ranking the app exposes on this store
  var SORTS = [
    { value: '', label: 'Relevance' },
    { value: 'price asc', label: 'Price: Low to high' },
    { value: 'price desc', label: 'Price: High to low' },
    { value: 'createdAt desc', label: 'New arrivals' },
    { value: 'displayText asc', label: 'Fragrance: A–Z' }
  ];
  var ICON_CHECK = '<svg viewBox="0 0 12 10" width="12" height="10" fill="none" aria-hidden="true"><path d="M1 5l3.2 3L11 1" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var allValues = {};   // key -> every value ever seen for the group (so lists never shrink while filtering)

  // desktop renders the groups as top-mode pills, mobile renders them inside the app's off-canvas drawer
  function nativeGroup(key) { return qs('.prefixbox-filter-container.' + key) || qs('.prefixbox-mobile-filter-container.' + key); }
  // The filter component's props carry { filter: { values: [{ name, count }] } } — read-only.
  function liveValues(key) {
    var g = nativeGroup(key);
    if (!g) return null;
    var fk = Object.keys(g).filter(function (k) { return k.indexOf('__reactFiber') === 0; })[0];
    var f = fk && g[fk];
    for (var i = 0; f && i < 8; i++) {
      var p = f.memoizedProps;
      if (p && p.filter && p.filter.values && p.filter.values.length !== undefined) {
        return p.filter.values.map(function (v) { return { name: String(v.name), count: v.count }; });
      }
      f = f.return;
    }
    return null;
  }
  function esc(v) { return String(v).replace(/'/g, "''"); }

  function readState() {
    var sp = new URL(location.href).searchParams;
    var state = { filters: {}, price: '', sort: sp.get('pf-sort-direction') || '' };
    var expr = (sp.get('pf-filters') || '').trim();
    if (expr.charAt(0) === '(' && expr.charAt(expr.length - 1) === ')') expr = expr.slice(1, -1);
    expr.split(/\)\)\s+and\s+\(\(/).forEach(function (part) {
      part = part.replace(/^\(+|\)+$/g, '');
      if (/\bprice\b/.test(part)) {
        var norm = part.replace(/\s+/g, ' ').trim();
        PRICE.buckets.forEach(function (b) { if (b.expr && ('(' + b.expr + ')').replace(/[()]/g, '') === norm.replace(/[()]/g, '')) state.price = b.id; });
        return;
      }
      var re = /(\w+)\s+eq\s+'((?:[^']|'')*)'/g, m;
      while ((m = re.exec(part))) { (state.filters[m[1]] = state.filters[m[1]] || []).push(m[2].replace(/''/g, "'")); }
    });
    return state;
  }
  function writeState(state) {
    var parts = [];
    Object.keys(state.filters).forEach(function (k) {
      var vals = state.filters[k];
      if (vals && vals.length) parts.push('(' + vals.map(function (v) { return '(' + k + " eq '" + esc(v) + "')"; }).join(' or ') + ')');
    });
    var bucket = PRICE.buckets.filter(function (b) { return b.id === state.price; })[0];
    if (bucket && bucket.expr) parts.push('(' + bucket.expr + ')');
    var url = new URL(location.href);
    if (parts.length) url.searchParams.set('pf-filters', '(' + parts.join(' and ') + ')'); else url.searchParams.delete('pf-filters');
    if (state.sort) url.searchParams.set('pf-sort-direction', state.sort); else url.searchParams.delete('pf-sort-direction');
    url.searchParams.delete('pf-page');   // every change starts from page 1 (also resets our retained pages)
    history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
  }

  var drawer = null, trigger = null, countPending = 0;

  function buildDrawer() {
    if (drawer) return drawer;
    drawer = el('div', 'mc-cr-drawer', '');
    drawer.innerHTML =
      '<div class="mc-cr-drawer__backdrop"></div>' +
      '<div class="mc-cr-drawer__panel" role="dialog" aria-modal="true" aria-label="Filter &amp; Sort" tabindex="-1">' +
        '<div class="mc-cr-drawer__head"><h2 class="mc-cr-drawer__title">Filter &amp; Sort</h2>' +
        '<button type="button" class="mc-cr-drawer__close" aria-label="Close">&times;</button></div>' +
        '<div class="mc-cr-drawer__body"></div>' +
        '<div class="mc-cr-drawer__foot"><button type="button" class="mc-cr-drawer__clear">Clear all</button>' +
        '<button type="button" class="mc-cr-drawer__apply"></button></div>' +
      '</div>';
    qs('.mc-cr-drawer__backdrop', drawer).addEventListener('click', closeDrawer);
    qs('.mc-cr-drawer__close', drawer).addEventListener('click', closeDrawer);
    qs('.mc-cr-drawer__apply', drawer).addEventListener('click', closeDrawer);
    qs('.mc-cr-drawer__clear', drawer).addEventListener('click', function () {
      var s = readState(); s.filters = {}; s.price = '';        // clears filters, keeps sort (contract)
      apply(s);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
    document.body.appendChild(drawer);
    return drawer;
  }

  function apply(state) {
    countPending = Date.now();
    drawer.classList.add('is-updating');
    writeState(state);
    renderChips();
    renderDrawer();
    // failsafe: if the app answers with an identical result set nothing re-renders — stop the dimmed state anyway
    setTimeout(function () { if (countPending) { countPending = 0; drawer.classList.remove('is-updating'); updateDrawerCount(); } }, 6000);
  }
  function openDrawer(mode, from) {
    buildDrawer();
    trigger = from || null;
    qsa('.mc-cr-drawer__group', drawer).forEach(function (g) { g.classList.remove('is-open'); });
    renderDrawer();
    var want = qs('.mc-cr-drawer__group[data-group="' + (mode === 'sort' ? 'sort' : 'custom_fragrance_type') + '"]', drawer);
    if (want) want.classList.add('is-open');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('mc-cr--drawer-open');
    var body = qs('.mc-cr-drawer__body', drawer);
    body.scrollTop = 0;
    if (mode === 'sort' && want) setTimeout(function () { want.scrollIntoView({ block: 'end' }); }, 60);
    qs('.mc-cr-drawer__close', drawer).focus();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('mc-cr--drawer-open');
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
  }

  function groupEl(id, label, hint) {
    var g = el('section', 'mc-cr-drawer__group', '');
    g.setAttribute('data-group', id);
    g.innerHTML =
      '<button type="button" class="mc-cr-drawer__gh" aria-expanded="false"><span class="mc-cr-drawer__gl"><span class="mc-cr-drawer__gname"></span><span class="mc-cr-drawer__ghint"></span></span>' + ICON_CHEVRON + '</button>' +
      '<div class="mc-cr-drawer__opts" role="group"></div>';
    qs('.mc-cr-drawer__gname', g).textContent = label;
    qs('.mc-cr-drawer__ghint', g).textContent = hint;
    qs('.mc-cr-drawer__opts', g).setAttribute('aria-label', label);
    qs('.mc-cr-drawer__gh', g).addEventListener('click', function () {
      var open = !g.classList.contains('is-open');
      g.classList.toggle('is-open', open);
      qs('.mc-cr-drawer__gh', g).setAttribute('aria-expanded', String(open));
    });
    return g;
  }
  function optionEl(cls, role, label, checked, disabled, onPick) {
    var b = el('button', 'mc-cr-drawer__opt ' + cls + (checked ? ' is-checked' : '') + (disabled ? ' is-disabled' : ''), '');
    b.type = 'button'; b.setAttribute('role', role); b.setAttribute('aria-checked', String(checked));
    if (disabled) b.disabled = true;
    b.innerHTML = '<span class="mc-cr-drawer__mark">' + ICON_CHECK + '</span><span class="mc-cr-drawer__olabel"></span>';
    qs('.mc-cr-drawer__olabel', b).textContent = label;
    b.addEventListener('click', onPick);
    return b;
  }

  // (Re)draws the drawer from the current URL state + the app's live facet values. Group open/closed
  // state and list scroll positions are preserved across redraws.
  function renderDrawer() {
    if (!drawer) return;
    var state = readState();
    var body = qs('.mc-cr-drawer__body', drawer);
    var open = {}, scrolls = {};
    qsa('.mc-cr-drawer__group', body).forEach(function (g) { open[g.getAttribute('data-group')] = g.classList.contains('is-open'); var o = qs('.mc-cr-drawer__opts', g); scrolls[g.getAttribute('data-group')] = o ? o.scrollTop : 0; });
    body.innerHTML = '';

    GROUPS.forEach(function (def) {
      var live = liveValues(def.key);
      if (live) { allValues[def.key] = allValues[def.key] || []; live.forEach(function (v) { if (allValues[def.key].indexOf(v.name) === -1) allValues[def.key].push(v.name); }); }
      var names = allValues[def.key] || [];
      if (!names.length) return;                               // app changed its internals — skip the group rather than show an empty one
      var available = live ? live.map(function (v) { return v.name; }) : names;
      var selected = state.filters[def.key] || [];
      var g = groupEl(def.key, def.label, selected.length ? selected.join(', ') : def.hint);
      if (def.scroll) g.classList.add('mc-cr-drawer__group--scroll');
      var opts = qs('.mc-cr-drawer__opts', g);
      names.forEach(function (name) {
        var checked = selected.indexOf(name) > -1;
        var disabled = !checked && available.indexOf(name) === -1;
        opts.appendChild(optionEl(def.multi ? 'mc-cr-drawer__opt--check' : 'mc-cr-drawer__opt--radio', def.multi ? 'checkbox' : 'radio', name, checked, disabled, function () {
          var s = readState(); var cur = s.filters[def.key] || [];
          if (def.multi) s.filters[def.key] = checked ? cur.filter(function (v) { return v !== name; }) : cur.concat([name]);
          else s.filters[def.key] = checked ? [] : [name];
          apply(s);
        }));
      });
      body.appendChild(g);
    });

    var pg = groupEl('price', PRICE.label, (PRICE.buckets.filter(function (b) { return b.id === state.price; })[0] || PRICE.buckets[0]).label.replace('Any price', PRICE.hint));
    var popts = qs('.mc-cr-drawer__opts', pg);
    PRICE.buckets.forEach(function (b) {
      popts.appendChild(optionEl('mc-cr-drawer__opt--right', 'radio', b.label, state.price === b.id, false, function () { var s = readState(); s.price = b.id; apply(s); }));
    });
    body.appendChild(pg);

    var sg = groupEl('sort', 'Sort by', (SORTS.filter(function (o) { return o.value === state.sort; })[0] || SORTS[0]).label);
    sg.classList.add('mc-cr-drawer__group--sort');
    var sopts = qs('.mc-cr-drawer__opts', sg);
    SORTS.forEach(function (o) {
      sopts.appendChild(optionEl('mc-cr-drawer__opt--right', 'radio', o.label, state.sort === o.value, false, function () { var s = readState(); s.sort = o.value; apply(s); }));
    });
    body.appendChild(sg);

    qsa('.mc-cr-drawer__group', body).forEach(function (g) {
      var id = g.getAttribute('data-group');
      if (open[id]) { g.classList.add('is-open'); qs('.mc-cr-drawer__gh', g).setAttribute('aria-expanded', 'true'); }
      var o = qs('.mc-cr-drawer__opts', g); if (o && scrolls[id]) o.scrollTop = scrolls[id];
    });
    updateDrawerCount();
  }
  function updateDrawerCount() {
    if (!drawer) return;
    // no products container = Prefixbox's zero-match state (it removes the result count with it)
    var n = qs('#pfbx-results-container .prefixbox-products-container') ? totalCount() : 0;
    qs('.mc-cr-drawer__apply', drawer).textContent = 'Show ' + n + ' product' + (n === 1 ? '' : 's');
  }

  // ── applied-filter chips (Figma 648-1565) ────────────────────────────────
  function renderChips() {
    var bar = qs('.mc-cr-toolbar');
    if (!bar) return;
    var row = qs('.mc-cr-chips');
    if (!row) {
      row = el('div', 'mc-cr-chips is-empty', '');
      row.setAttribute('aria-label', 'Applied filters');
      bar.parentNode.insertBefore(row, bar.nextSibling);
    }
    var state = readState(), items = [];
    GROUPS.forEach(function (def) { (state.filters[def.key] || []).forEach(function (v) { items.push({ label: v, key: def.key, value: v }); }); });
    var bucket = PRICE.buckets.filter(function (b) { return b.id === state.price; })[0];
    if (bucket && bucket.id) items.push({ label: bucket.label, key: 'price', value: '' });
    var sortOpt = SORTS.filter(function (o) { return o.value === state.sort; })[0];
    if (sortOpt && sortOpt.value) items.push({ label: COPY.sortBy + ' ' + sortOpt.label, key: 'sort', value: '' });
    var sig = JSON.stringify(items);
    if (row.getAttribute('data-sig') === sig) return;
    row.setAttribute('data-sig', sig);
    row.innerHTML = '';
    row.classList.toggle('is-empty', !items.length);
    if (!items.length) return;
    items.forEach(function (it) {
      var chip = el('button', 'mc-cr-chip', '<span class="mc-cr-chip__label"></span><span class="mc-cr-chip__x" aria-hidden="true">&times;</span>');
      chip.type = 'button';
      qs('.mc-cr-chip__label', chip).textContent = it.label;
      chip.setAttribute('aria-label', 'Remove filter: ' + it.label);
      chip.addEventListener('click', function () {
        var s = readState();
        if (it.key === 'price') s.price = '';
        else if (it.key === 'sort') s.sort = '';
        else s.filters[it.key] = (s.filters[it.key] || []).filter(function (v) { return v !== it.value; });
        writeState(s);
        renderChips();
      });
      row.appendChild(chip);
    });
    var clear = el('button', 'mc-cr-chips__clear', COPY.clearAll);
    clear.type = 'button';
    clear.addEventListener('click', function () { var s = readState(); s.filters = {}; s.price = ''; s.sort = ''; writeState(s); renderChips(); });
    row.appendChild(clear);
  }

  // ── toolbar (Filter / Sort open the same drawer, per the contract) ──────
  function buildToolbar(search) {
    if (qs('.mc-cr-toolbar', search)) return;
    var fsc = qs('.filter-and-sort-section-container', search);
    var bar = el('div', 'mc-cr-toolbar', '');
    var filterBtn = el('button', 'mc-cr-btn mc-cr-btn--filter', ICON_FILTER + '<span>' + COPY.filter + '</span>');
    filterBtn.type = 'button'; filterBtn.setAttribute('aria-haspopup', 'dialog');
    filterBtn.addEventListener('click', function () { openDrawer('filter', filterBtn); });
    var sortBtn = el('button', 'mc-cr-btn mc-cr-btn--sort', '<span>' + COPY.sort + '</span>' + ICON_CHEVRON);
    sortBtn.type = 'button'; sortBtn.setAttribute('aria-haspopup', 'dialog');
    sortBtn.addEventListener('click', function () { openDrawer('sort', sortBtn); });
    bar.appendChild(filterBtn); bar.appendChild(sortBtn);
    if (fsc) fsc.parentNode.insertBefore(bar, fsc); else search.insertBefore(bar, search.firstChild);
  }

  // ── 3. grid decorations (re-applied on every Prefixbox render) ───────────
  // Real product cards only: Prefixbox also seats merchandising banner tiles and loading skeletons in the
  // grid with the same container class but no product name (the banners are hidden by CSS).
  var cards = function (grid) {
    return qsa(':scope > .prefixbox-product-container', grid).filter(function (c) { return !!c.querySelector('.prefixbox-product-name'); });
  };

  // The suffix to drop from product names comes from the section setting ("3-Wick Candle" on the test
  // collection). Spaces and hyphens are interchangeable so "3 Wick Candle" matches too. Blank = leave names alone.
  var STRIP_RE = (function () {
    var s = (CFG.stripSuffix || '').trim();
    if (!s) return null;
    var body = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[-\s]+/g, '[-\\s]+');
    try { return new RegExp('\\s*' + body + '\\s*$', 'i'); } catch (e) { return null; }
  })();
  function stripNames(grid) {
    if (!STRIP_RE) return;
    // text-based (no marker flag) so a node React reuses for a different product is re-stripped
    qsa('.prefixbox-product-name a', grid).forEach(function (a) {
      if (STRIP_RE.test(a.textContent)) a.textContent = a.textContent.replace(STRIP_RE, '').trim();
    });
  }

  // Where the promo tile sits. Cards fill the grid row-major, so after N cards the next free cell is
  // index N: row = floor(N / cols) + 1, column = (N % cols) + 1. The tile is two columns wide, so if it
  // would overhang the right edge it starts the next row instead. Columns are read from the grid's own
  // computed template, which is whatever the CSS media queries decided — no breakpoint list to keep in sync.
  function promoCell(grid, promo) {
    // Measure with our own placement cleared first. A tile pinned to e.g. column 4 of a 2-column grid
    // CREATES implicit columns 3-5, so gridTemplateColumns then reports the columns the tile just
    // invented rather than the ones the CSS declared. Measuring while pinned made the desktop cell
    // self-perpetuating: it read 5, re-applied 4/span 2, and never recovered on resize.
    // Reading a computed value after the write forces a style flush, so this measures the real grid.
    if (promo) { promo.style.gridColumn = ''; promo.style.gridRow = ''; }
    var cols = 5;
    try {
      var t = window.getComputedStyle(grid).gridTemplateColumns;
      var n = t && t !== 'none' ? t.trim().split(/\s+/).length : 0;
      if (n > 0) cols = n;
    } catch (e) { /* keep the default */ }
    var idx = PROMO_AFTER_CARD;
    var row = Math.floor(idx / cols) + 1;
    var col = (idx % cols) + 1;
    var span = Math.min(2, cols);
    if (col + span - 1 > cols) { row += 1; col = 1; }       // would overhang — start the next row
    return { row: row, col: col, span: span };
  }

  function placePromo(grid) {
    if (!PROMO.desktop) return;                              // no custom.mid_collection_image on this collection — no tile
    var list = cards(grid);
    var promo = qs('.mc-cr-promo', grid);
    if (!promo) {
      var href = CFG.promo.link;
      if (!href) {                                           // no link set — fall back to the nav's plug-ins entry, as the test did
        var nav = qsa('header a, nav a').filter(function (a) { return /plug-?ins/i.test(a.textContent); })[0];
        href = (nav && nav.getAttribute('href')) || '/collections/scented-plug-ins';
      }
      promo = el('a', 'mc-cr-promo', '<img class="mc-cr-promo__img" alt="" loading="lazy">');
      qs('.mc-cr-promo__img', promo).alt = CFG.promo.alt || '';
      promo.href = href;
    }
    // A plain <img> whose src we pick, rather than <picture>/<source media>: picture chooses its source
    // when the image loads and does not dependably re-run that choice when the viewport crosses the
    // breakpoint later, so dragging a desktop window down to phone width kept the desktop crop. This
    // runs on every render and on resize, so the crop always matches the grid it is sitting in.
    var pimg = qs('.mc-cr-promo__img', promo);
    var wantSrc = (isMobile() && PROMO.mobile) ? PROMO.mobile : PROMO.desktop;
    if (pimg && pimg.getAttribute('src') !== wantSrc) pimg.setAttribute('src', wantSrc);

    var anchor = list[PROMO_AFTER_CARD - 1];
    if (!anchor) { if (promo.parentNode) promo.parentNode.removeChild(promo); return; }
    var cell = promoCell(grid, promo);
    promo.style.gridRow = String(cell.row);
    promo.style.gridColumn = cell.span > 1 ? (cell.col + ' / span ' + cell.span) : String(cell.col);
    if (promo.previousElementSibling !== anchor) grid.insertBefore(promo, anchor.nextSibling);
  }

  // ── filter / sort transition (no flash) ──────────────────────────────────
  var ghost = null, ghostTimer = null, quietTimer = null, releasePending = false, holdSig = null;
  var gridSig = function (grid) { return cards(grid).slice(0, 6).map(function (c) { var n = c.querySelector('.prefixbox-product-name'); return n ? n.textContent.trim() : ''; }).join('|'); };
  function holdGrid() {
    var results = qs('#pfbx-results-container');
    var grid = results && qs('.mc-cr-grid', results);
    if (!grid || !cards(grid).length) return;
    releaseGrid();
    holdSig = { count: totalCount(), sig: gridSig(grid) };
    ghost = grid.cloneNode(true);
    ghost.classList.remove('mc-cr-grid');
    ghost.classList.add('mc-cr-ghost');
    qsa('iframe', ghost).forEach(function (n) { n.parentNode.removeChild(n); });
    grid.parentNode.insertBefore(ghost, grid.nextSibling);
    results.style.minHeight = results.offsetHeight + 'px';
    ghostTimer = setTimeout(releaseGrid, 3000);   // failsafe: never hold the old results longer than this
  }
  function releaseGrid() {
    clearTimeout(ghostTimer); clearTimeout(quietTimer);
    releasePending = false;
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
    ghost = null;
    var results = qs('#pfbx-results-container');
    if (results) results.style.minHeight = '';
  }
  // Called on every render while the snapshot is up. A render that shows the same count and the same first
  // products as before is not trusted until the grid has been quiet for a second; a changed count or order
  // is the new set and releases straight away.
  function scheduleRelease(grid) {
    if (!ghost || releasePending) return;
    clearTimeout(quietTimer);
    var changed = totalCount() !== holdSig.count || gridSig(grid) !== holdSig.sig;
    if (changed) releaseAfterImages(grid);
    else quietTimer = setTimeout(function () { releaseAfterImages(grid); }, 1000);
  }
  function releaseAfterImages(grid) {
    if (!ghost || releasePending) return;
    releasePending = true;
    var imgs = cards(grid).slice(0, 10).map(function (c) { return c.querySelector('img'); }).filter(function (i) { return i && !i.complete; });
    var done = function () { if (releasePending) releaseGrid(); };
    if (!imgs.length) { setTimeout(done, 60); return; }   // let the new cards paint once (a timer, not rAF: rAF stalls in a background tab)
    var left = imgs.length, cap = setTimeout(done, 1200);
    var tick = function () { if (--left <= 0) { clearTimeout(cap); done(); } };
    imgs.forEach(function (i) { i.addEventListener('load', tick); i.addEventListener('error', tick); });
  }

  // ── 4. paging facade (both directions) ───────────────────────────────────
  var retained = [];        // cards of pages below the app's page, in display order (kept ahead of its cards)
  var retainedAfter = [];   // cards of pages above the app's page, in display order (kept behind its cards)
  var pageSize = 0;         // the app's products per page, learned from the largest page it has rendered
  var loadingMore = false;  // true between a Load more / Show previous click and the app's render
  var loadDir = 'next';     // which way the in-flight request goes
  var lowPage = 1, highPage = 1;   // the contiguous range of pages on screen
  var currentPage = 1;      // the app's page (the one in the URL) — the newest render
  var targetPage = 1;       // the page we asked for
  var anchor = null, anchorTimer = null;   // { el, docTop } — the card to keep in place while a previous page lands above it
  var activePage = function () {
    var n = parseInt(new URL(location.href).searchParams.get('pf-page') || '1', 10);
    return isNaN(n) || n < 1 ? 1 : n;
  };
  // the highest page number the app's pager offers (it always lists the last page next to "…")
  var pagerLast = function (results) {
    return qsa('#paginator button', results).reduce(function (m, b) { var n = parseInt(b.value || b.textContent, 10); return isNaN(n) ? m : Math.max(m, n); }, 0);
  };
  var pageSizeInEffect = function () { return pageSize > PAGE_SIZE ? pageSize : PAGE_SIZE; };
  var lastPage = function (results) {
    var size = pageSizeInEffect();
    var byCount = Math.ceil((totalCount() || 0) / size) || 0;
    if (size === PAGE_SIZE) return byCount || currentPage;
    var byPager = pagerLast(results);
    var n = Math.min(byPager || Infinity, byCount || Infinity);
    return isFinite(n) ? n : currentPage;
  };
  // "159 results" lives in Prefixbox's (hidden) sort/result row, which sits beside — not inside — the
  // results container, so look it up from the search container.
  var totalCount = function () {
    var t = qs('#prefixbox-search-container .prefixbox-result-text');
    var m = t && t.textContent.match(/\d[\d,]*/);
    return m ? parseInt(m[0].replace(/,/g, ''), 10) : 0;
  };

  // Retained cards lost their React click handler with their fiber; the app's cart.min.js listens on
  // window for this event and reads the <form> around the button, exactly as the native click does.
  function wireRetainedAtc(card) {
    var btn = qs('form button[name="add"]', card);
    if (!btn || btn.dataset.mcAtc) return;
    btn.dataset.mcAtc = '1';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      btn.dispatchEvent(new CustomEvent('pfbx-category-add-to-cart', { bubbles: true }));
    });
  }

  function buildLoadMore(results) {
    var box = qs('.mc-cr-loadmore', results);
    if (box) return box;
    box = el('div', 'mc-cr-loadmore',
      '<div class="mc-cr-loadmore__summary"><p class="mc-cr-loadmore__text"></p>' +
      '<div class="mc-cr-loadmore__track"><div class="mc-cr-loadmore__fill"></div></div></div>' +
      '<button type="button" class="mc-cr-btn mc-cr-btn--more">' + COPY.loadMore + '</button>');
    qs('.mc-cr-btn--more', box).addEventListener('click', function () {
      // never ask the app for a page past the last: it renders an empty grid with no message for one.
      if (loadingMore || highPage + 1 > lastPage(results)) return;
      box.classList.add('is-loading');
      requestPage(results, highPage + 1, 'next');
    });
    var pager = qs('.pfbx-next-btn', results);
    var pagerWrap = pager ? pager.closest('#pfbx-results-container > *') : null;
    if (pagerWrap) results.insertBefore(box, pagerWrap); else results.appendChild(box);
    return box;
  }

  function buildLoadPrev(results) {
    var box = qs('.mc-cr-loadprev', results);
    if (box) return box;
    box = el('div', 'mc-cr-loadprev is-hidden', '<button type="button" class="mc-cr-btn mc-cr-btn--prev">' + COPY.loadPrev + '</button>');
    qs('.mc-cr-btn--prev', box).addEventListener('click', function () {
      if (loadingMore || lowPage <= 1) return;
      box.classList.add('is-loading');
      requestPage(results, lowPage - 1, 'prev');
    });
    var inner = qs('#results', results);
    if (inner) results.insertBefore(box, inner); else results.insertBefore(box, results.firstChild);
    return box;
  }

  // Page through the app's URL state, NOT the pager buttons: the theme's own script wires every
  // paginator button to scroll the results to the top of the viewport 100ms after the click — exactly
  // the jump a "Load more" must never make.
  function requestPage(results, page, dir) {
    var grid = qs('.mc-cr-grid', results);
    var shown = cards(grid);
    if (dir === 'next') { retained = shown; retainedAfter = []; anchor = null; }
    else { retainedAfter = shown; retained = []; clearTimeout(anchorTimer); anchor = shown[0] ? { el: shown[0], docTop: shown[0].getBoundingClientRect().top + window.scrollY } : null; }
    loadingMore = true; loadDir = dir; targetPage = page;
    results.style.minHeight = results.offsetHeight + 'px';   // no collapse while the app swaps its page
    var url = new URL(location.href);
    url.searchParams.set('pf-page', String(page));
    history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    setTimeout(function () {   // failsafe
      if (!loadingMore) return;
      loadingMore = false;
      qsa('.mc-cr-loadmore, .mc-cr-loadprev', results).forEach(function (b) { b.classList.remove('is-loading'); });
      results.style.minHeight = '';
    }, 15000);
  }

  function updateLoadMore(results) {
    if (PAGING === 'buttons') { updatePager(results); return; }
    var box = buildLoadMore(results);
    var prev = buildLoadPrev(results);
    var grid = qs('.mc-cr-grid', results);
    var shown = cards(grid).length;
    var total = totalCount() || shown;
    // only touch the DOM when the value changes — the results observer would otherwise see our own
    // text-node swap as a mutation and re-run decorate() forever
    var text = qs('.mc-cr-loadmore__text', box);
    var viewed = COPY.viewed(Math.min(shown, total), total);
    if (text.textContent !== viewed) text.textContent = viewed;
    qs('.mc-cr-loadmore__fill', box).style.width = (total ? Math.min(100, Math.round(shown / total * 100)) : 100) + '%';
    box.classList.toggle('is-complete', shown >= total || highPage >= lastPage(results));
    box.classList.remove('is-loading');
    prev.classList.toggle('is-hidden', lowPage <= 1);
    prev.classList.remove('is-loading');
    if (!loadingMore && !ghost) results.style.minHeight = '';
  }

  // ── page buttons (PAGING = 'buttons') ────────────────────────────────────
  function buildPager(results) {
    var box = qs('.mc-cr-pager', results);
    if (box) return box;
    box = el('nav', 'mc-cr-pager',
      '<div class="mc-cr-pager__pages"></div>' +
      '<div class="mc-cr-pager__summary"><p class="mc-cr-loadmore__text"></p>' +
      '<div class="mc-cr-loadmore__track"><div class="mc-cr-loadmore__fill"></div></div></div>');
    box.setAttribute('aria-label', 'Pagination');
    qs('.mc-cr-pager__pages', box).addEventListener('click', function (e) {
      var b = e.target.closest('button[data-page]');
      if (!b || b.disabled) return;
      goToPage(results, parseInt(b.getAttribute('data-page'), 10));
    });
    var pager = qs('.pfbx-next-btn', results);
    var pagerWrap = pager ? pager.closest('#pfbx-results-container > *') : null;
    if (pagerWrap) results.insertBefore(box, pagerWrap); else results.appendChild(box);
    // the set of numbers differs between mobile and desktop — re-render when the breakpoint flips
    var mql = window.matchMedia(MOBILE_MQ), onFlip = function () { updatePager(results); var g = qs('.mc-cr-grid', results); if (g) placePromo(g); };
    if (mql.addEventListener) mql.addEventListener('change', onFlip); else if (mql.addListener) mql.addListener(onFlip);
    return box;
  }
  function goToPage(results, page) {
    if (page < 1 || page === currentPage || page > lastPage(results)) return;
    var url = new URL(location.href);
    if (page === 1) url.searchParams.delete('pf-page'); else url.searchParams.set('pf-page', String(page));
    history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));   // the app re-reads ?pf-page and renders the page
    var top = qs('.mc-cr-toolbar');
    var y = Math.max(0, (top ? top.getBoundingClientRect().top + window.scrollY : 0) - 16);
    if (document.hidden) window.scrollTo(0, y);                  // smooth scrolling never finishes in a background tab
    else window.scrollTo({ top: y, behavior: 'smooth' });
  }
  // The page numbers to show. Desktop: all of them up to 7, otherwise first, last and the current one's
  // neighbours. Mobile: first, current, the next one and last (1 2 3 … 9 on page 2), so the next page is
  // always a number to tap, not only the arrow. Four numbers and two gaps is what fits a 343px row.
  function pageWindow(cur, last, mobile) {
    var out = [], i;
    if (mobile) {
      var set = [1, cur, cur + 1, last];
      if (cur === last) set.push(last - 1);
      set = set.filter(function (n, idx) { return n >= 1 && n <= last && set.indexOf(n) === idx; }).sort(function (a, b) { return a - b; });
      set.forEach(function (n, idx) { if (idx && n - set[idx - 1] > 1) out.push('…'); out.push(n); });
      return out;
    }
    if (last <= 7) { for (i = 1; i <= last; i++) out.push(i); return out; }
    out.push(1);
    if (cur > 3) out.push('…');
    for (i = Math.max(2, cur - 1); i <= Math.min(last - 1, cur + 1); i++) out.push(i);
    if (cur < last - 2) out.push('…');
    out.push(last);
    return out;
  }
  function updatePager(results) {
    var box = buildPager(results);
    var grid = qs('.mc-cr-grid', results);
    var onPage = cards(grid).length;
    var total = totalCount() || onPage;
    var last = Math.max(1, lastPage(results));
    var cur = Math.min(Math.max(1, currentPage), last);
    var mobile = isMobile();
    var sig = cur + '/' + last + (mobile ? '/m' : '/d');
    var pages = qs('.mc-cr-pager__pages', box);
    if (pages.getAttribute('data-sig') !== sig) {
      pages.setAttribute('data-sig', sig);
      var html = '<button type="button" class="mc-cr-pager__btn mc-cr-pager__btn--prev" data-page="' + (cur - 1) + '" aria-label="' + COPY.pagePrev + '"' + (cur <= 1 ? ' disabled' : '') + '>&lsaquo;</button><span class="mc-cr-pager__nums">';
      pageWindow(cur, last, mobile).forEach(function (p) {
        html += p === '…' ? '<span class="mc-cr-pager__gap" aria-hidden="true">…</span>'
          : '<button type="button" class="mc-cr-pager__btn' + (p === cur ? ' is-current' : '') + '" data-page="' + p + '" aria-label="' + COPY.pageN(p) + '"' + (p === cur ? ' aria-current="page"' : '') + '>' + p + '</button>';
      });
      html += '</span><button type="button" class="mc-cr-pager__btn mc-cr-pager__btn--next" data-page="' + (cur + 1) + '" aria-label="' + COPY.pageNext + '"' + (cur >= last ? ' disabled' : '') + '>&rsaquo;</button>';
      pages.innerHTML = html;
    }
    box.classList.toggle('is-single', last <= 1);
    var seen = Math.min(total, (cur - 1) * pageSizeInEffect() + onPage);   // products viewed up to and including this page
    var text = qs('.mc-cr-loadmore__text', box);
    var viewed = COPY.viewed(seen, total);
    if (text.textContent !== viewed) text.textContent = viewed;
    qs('.mc-cr-loadmore__fill', box).style.width = (total ? Math.min(100, Math.round(seen / total * 100)) : 100) + '%';
    if (!ghost) results.style.minHeight = '';
  }

  // ── remembered pages (sessionStorage) ────────────────────────────────────
  var STORE_KEY = 'mc-cr-pages';
  var pagesKey = function () { var sp = new URL(location.href).searchParams; return location.pathname + '|' + (sp.get('pf-filters') || '') + '|' + (sp.get('pf-sort-direction') || '') + '|' + PAGE_SIZE; };
  var saveTimer = null, restoreFor = 0;
  function scheduleSave() { clearTimeout(saveTimer); saveTimer = setTimeout(savePages, 600); }
  function savePages() {
    var grid = qs('#pfbx-results-container .mc-cr-grid');
    var list = grid ? cards(grid) : [];
    if (loadingMore || !list.length || !pageSize) return;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ key: pagesKey(), low: lowPage, high: highPage, pageSize: pageSize, cards: list.map(function (c) { return c.outerHTML; }) }));
    } catch (e) { /* quota / storage off — the fallback button still covers it */ }
  }
  function wipePages() { try { sessionStorage.removeItem(STORE_KEY); } catch (e) {} }
  // puts the remembered pages back around the app's page — true when it did
  function restorePages(grid, page) {
    var data = null;
    try { data = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) {}
    if (!data || data.key !== pagesKey() || !data.cards || !data.cards.length || !data.pageSize) return false;
    if (page < data.low || page > data.high) return false;      // the remembered range doesn't include this page
    var box = document.createElement('div'), before = [], after = [];
    data.cards.forEach(function (html, i) {
      var p = data.low + Math.floor(i / data.pageSize);         // every remembered page but the last is full
      if (p === page) return;                                    // the app renders this one itself
      box.innerHTML = html;
      var node = box.firstElementChild;
      if (!node) return;
      qsa('[data-mc-atc]', node).forEach(function (b) { b.removeAttribute('data-mc-atc'); });
      (p < page ? before : after).push(node);
    });
    if (!before.length && !after.length) return false;
    retained = before; retainedAfter = after;
    lowPage = data.low; highPage = data.high;
    if (pageSize < data.pageSize) pageSize = data.pageSize;
    before.concat(after).forEach(wireRetainedAtc);
    var first = cards(grid)[0];                                  // keep the app's page where it is while pages land above it
    clearTimeout(anchorTimer);
    anchor = first ? { el: first, docTop: first.getBoundingClientRect().top + window.scrollY } : null;
    return true;
  }

  // ── return to the product that was clicked ───────────────────────────────
  var RETURN_KEY = 'mc-cr-return';
  var returnTarget = null, returnDone = false, returnTimer = null;
  var returnKey = function () { return pagesKey() + '|' + activePage(); };
  function rememberClick(e) {
    var a = e.target && e.target.closest && e.target.closest('.mc-cr-grid a[href*="/products/"]');
    if (!a) return;
    try { sessionStorage.setItem(RETURN_KEY, JSON.stringify({ key: returnKey(), href: a.getAttribute('href').split('?')[0], t: Date.now() })); } catch (err) {}
  }
  function loadReturnTarget() {
    var d = null;
    try { d = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null'); } catch (e) {}
    if (!d || !d.href || d.key !== returnKey() || Date.now() - (d.t || 0) > 30 * 60000) return null;
    return d;
  }
  function returnToProduct(grid) {
    if (returnDone || !returnTarget) return;
    var link = qsa('.prefixbox-product-container a[href*="/products/"]', grid).filter(function (a) { return a.getAttribute('href').split('?')[0] === returnTarget.href; })[0];
    var card = link && link.closest('.prefixbox-product-container');
    if (!card) return;
    returnDone = true;
    try { sessionStorage.removeItem(RETURN_KEY); } catch (e) {}
    clearTimeout(returnTimer);
    returnTimer = setTimeout(function () {                      // let the row settle before measuring
      window.scrollTo(0, Math.max(0, card.getBoundingClientRect().top + window.scrollY - 120));
      card.classList.add('mc-cr-card--returned');
      setTimeout(function () { card.classList.remove('mc-cr-card--returned'); }, 2500);
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    }, 150);
  }

  // ── 5. about accordion (theme bottom description) ────────────────────────
  function buildAbout() {
    var sec = section('collection_bottom_description');
    if (!sec || qs('.mc-cr-about__head', sec)) return;
    var cbd = qs('.cbd', sec) || sec;
    var inner = qs('.cbd__inner', sec);
    if (!inner) return;
    sec.classList.add('mc-cr-about');
    var head = el('button', 'mc-cr-about__head', '<span></span>' + ICON_CHEVRON);
    qs('span', head).textContent = COPY.about;
    head.type = 'button'; head.setAttribute('aria-expanded', 'false');
    head.addEventListener('click', function () {
      var open = !sec.classList.contains('is-open');
      sec.classList.toggle('is-open', open);
      head.setAttribute('aria-expanded', String(open));
      // the theme's own collapsible inside is expanded once so the full copy shows without a second toggle
      var t = qs('.cbd__toggle', sec);
      if (open && t && t.getAttribute('aria-expanded') === 'false') t.click();
    });
    cbd.insertBefore(head, inner);
  }

  // ── empty state (Figma 646-3111) ─────────────────────────────────────────
  function renderEmpty(results) {
    if (!qs('.prefixbox-no-result-text', results)) return;   // no grid AND no message = the app's error state — left as is
    if (qs('.mc-cr-empty', results)) return;
    var box = el('div', 'mc-cr-empty', '<p class="mc-cr-empty__title"></p><p class="mc-cr-empty__text"></p>');
    qs('.mc-cr-empty__title', box).textContent = COPY.emptyTitle;
    qs('.mc-cr-empty__text', box).textContent = COPY.emptyText;
    var btn = el('button', 'mc-cr-btn mc-cr-btn--clear', COPY.emptyClear);
    btn.type = 'button';
    btn.addEventListener('click', function () { var s = readState(); s.filters = {}; s.price = ''; writeState(s); renderChips(); });
    box.appendChild(btn);
    (qs('#results', results) || results).appendChild(box);
  }
  function removeEmpty(results) {
    var box = qs('.mc-cr-empty', results);
    if (box) box.parentNode.removeChild(box);
  }

  // ── orchestration ────────────────────────────────────────────────────────
  function decorate(results) {
    var grid = qsa('.prefixbox-product-container', results).filter(function (c) { return !c.closest('.mc-cr-ghost'); })[0];
    grid = grid && grid.parentElement;
    var box = qs('.mc-cr-loadmore', results);
    if (!grid) {
      // Zero matches: Prefixbox swaps the whole products container for its "No results found." message.
      if (box) box.classList.add('is-empty');
      var pg = qs('.mc-cr-pager', results);
      if (pg) pg.classList.add('is-empty');
      renderEmpty(results);
      releaseGrid();
      retained = []; retainedAfter = []; loadingMore = false;
      wipePages();
      var prevBox = qs('.mc-cr-loadprev', results);
      if (prevBox) prevBox.classList.add('is-hidden');
      renderChips();
      if (drawer && drawer.classList.contains('is-open')) { renderDrawer(); countPending = 0; drawer.classList.remove('is-updating'); }
      return;
    }
    if (box) box.classList.remove('is-empty');
    var pgBox = qs('.mc-cr-pager', results);
    if (pgBox) pgBox.classList.remove('is-empty');
    removeEmpty(results);
    grid.classList.add('mc-cr-grid');
    results.classList.add('mc-cr-results');

    var page = activePage();
    var kept = retained.concat(retainedAfter);
    var fresh = cards(grid).filter(function (c) { return kept.indexOf(c) === -1; });
    var oversize = fresh.length > PAGE_SIZE;
    // This page was fetched before our page-size rewrite: the resource log says so, or the render has 24
    // cards. Ask for it again, at most once every few seconds and a handful of times per visit.
    if (fresh.length && (oversize || stalePages[page]) && requeries < 6) {
      if (Date.now() - requeryAt <= 2500) { setTimeout(function () { decorate(results); }, 2600); return; }   // too soon — look again shortly
      delete stalePages[page];
      requeries++; requeryAt = Date.now();
      pageSize = 0;                                              // learn the page size from the re-queried render, not this one
      requery();
      return;                                                    // this render is replaced within a second; decorate runs again then
    }
    delete stalePages[page];                                     // settled (or capped) — don't re-ask later
    if (fresh.length > pageSize && (!oversize || Date.now() - requeryAt > 3000)) pageSize = fresh.length;
    if (loadingMore && page === targetPage) {
      // Prefixbox has rendered the page we asked for. It shows name-less skeleton tiles while the
      // request is in flight, so wait until real cards exist before declaring the page loaded.
      if (!fresh.length) return;
      kept.forEach(wireRetainedAtc);
      currentPage = page;
      if (loadDir === 'next') highPage = page; else lowPage = page;
      loadingMore = false;
      scheduleSave();
    } else if (page === currentPage && !loadingMore) {
      // a re-render of the page we already show (resize, lazy images…) — keep what we have
    } else if (!loadingMore || page !== targetPage) {
      // any render we did not ask for (filter, sort, popstate, a direct load of ?pf-page=N) is a fresh result set
      kept.forEach(function (c) { if (c.parentNode === grid) grid.removeChild(c); });
      retained = []; retainedAfter = [];
      loadingMore = false;
      currentPage = page; lowPage = page; highPage = page;
      restoreFor = (PAGING === 'loadmore' && page > 1) ? page : 0;   // a page other than 1 may have remembered neighbours
    }
    if (restoreFor && fresh.length) {                            // once the app's page has real cards, put the remembered pages back
      if (restoreFor === page && restorePages(grid, page)) scheduleSave();
      restoreFor = 0;
    }
    // Kept pages sit around the app's page in their original order. React only repositions its own nodes,
    // but it can drop one in front of ours, so re-seat any kept card that is missing or out of place.
    // Nodes are moved only when out of place — a no-op move would still be a mutation and re-run this.
    var isCard = function (n) { return n && n.classList && n.classList.contains('prefixbox-product-container'); };
    var prevCard = function (n) { n = n.previousElementSibling; while (n && !isCard(n)) n = n.previousElementSibling; return n; };
    if (retained.length) {
      var firstFresh = fresh[0] || null;
      var prev = null;   // the kept card that must precede the next one (our promo tile may sit between)
      retained.forEach(function (c) {
        var inPlace = c.parentNode === grid && prevCard(c) === prev &&
          (!firstFresh || !!(c.compareDocumentPosition(firstFresh) & Node.DOCUMENT_POSITION_FOLLOWING));
        if (!inPlace) grid.insertBefore(c, prev ? prev.nextSibling : grid.firstChild);
        prev = c;
      });
    }
    if (retainedAfter.length && fresh.length) {
      var prevA = fresh[fresh.length - 1];   // the first kept-after card directly follows the last fresh card
      retainedAfter.forEach(function (c) {
        var inPlace = c.parentNode === grid && prevCard(c) === prevA;
        if (!inPlace) grid.insertBefore(c, prevA.nextSibling);
        prevA = c;
      });
    }
    stripNames(grid);
    placePromo(grid);
    updateLoadMore(results);
    renderChips();
    if (anchor && !loadingMore && anchor.el.isConnected) {
      // a page is landing above the visitor's cards — keep the card they were looking at where it was
      var docTop = anchor.el.getBoundingClientRect().top + window.scrollY;
      if (docTop !== anchor.docTop) { window.scrollBy(0, docTop - anchor.docTop); anchor.docTop = docTop; }
      clearTimeout(anchorTimer);
      anchorTimer = setTimeout(function () { anchor = null; }, 800);
    }
    if (ghost && fresh.length && !loadingMore) scheduleRelease(grid);   // the new result set is in — let its images land, then drop the snapshot
    if (fresh.length) returnToProduct(grid);
    // the drawer mirrors the app's state: refresh its counts/availability after every render
    if (drawer && drawer.classList.contains('is-open')) { renderDrawer(); if (countPending) { countPending = 0; drawer.classList.remove('is-updating'); } }
  }

  // Phase A — everything that exists server-side: scope the page, rebuild the header, add the toolbar,
  // wrap the About block, then reveal. The CSS keeps `.collection-main--wrapper` at opacity 0 until
  // html.mc-cr--ready so the original layout never flashes (with a 5s pure-CSS failsafe reveal).
  function initPage() {
    if (document.documentElement.classList.contains('mc-cr--ready')) return;
    document.documentElement.classList.add('mc-cr', 'mc-cr--v' + VARIANT);
    initIntro();
    var search = qs('#prefixbox-search-container');
    if (search) buildToolbar(search);
    renderChips();
    window.addEventListener('popstate', renderChips);   // back/forward to an identical result set re-renders nothing else
    window.addEventListener('popstate', function () {   // every state change but Load more (which keeps its cards anyway)
      if (loadingMore) return;
      holdGrid();
      // the pages kept by Load more belong to the outgoing result set — drop them now (under the snapshot)
      retained.concat(retainedAfter).forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
      retained = []; retainedAfter = []; currentPage = lowPage = highPage = 1;
      wipePages();   // remembered pages belong to the outgoing filters/sort
    });
    buildAbout();
    document.addEventListener('click', rememberClick, true);   // product clicks, before the app's own handlers
    returnTarget = loadReturnTarget();
    if (returnTarget && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';                    // we place the visitor ourselves once the card exists
      setTimeout(function () { if (!returnDone) history.scrollRestoration = 'auto'; }, 15000);
    }
    document.documentElement.classList.add('mc-cr--ready');
  }

  // Phase B — once Prefixbox has rendered its first cards (the grid CSS is keyed on the app's own
  // container, so those cards are styled the instant they appear).
  function initGrid() {
    var search = qs('#prefixbox-search-container');
    var results = qs('#pfbx-results-container');
    if (!search || !results) return;
    initPage();
    buildToolbar(search);   // no-op when phase A already built it
    decorate(results);

    // The tile's cell is inline, so it has to be recomputed whenever the column count can change.
    // The pager's matchMedia listener only covers the mobile breakpoint and would miss the 1440 step
    // (5 -> 4 columns), so resize is handled here too. Debounced; writing inline styles does not feed
    // the observer below, which watches childList/characterData and not attributes.
    // The products per page follow the columns too: when a breakpoint changes the count, forget what was
    // learned at the old size and re-ask for the current page, so its last row fills at the new width.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var size = pageSizeFor(gridCols());
        if (size !== PAGE_SIZE) {
          PAGE_SIZE = size;
          pageSize = 0;
          retained = []; retainedAfter = [];
          lowPage = highPage = currentPage;
          wipePages();
          stalePages[activePage()] = true;
          decorate(results);
          return;
        }
        var g = qs('.mc-cr-grid', results);
        if (g) placePromo(g);
        if (PAGING === 'buttons') updatePager(results);
      }, 150);
    }, { passive: true });

    // Prefixbox swaps the card nodes on every filter / sort / page change; re-apply after each render.
    // Mutations inside our own blocks are ignored so that decorate() can never trigger itself.
    var pending = null;
    var ours = function (node) { return !!(node && node.nodeType === 1 ? node.closest('.mc-cr-loadmore, .mc-cr-pager, .mc-cr-ghost') : node && node.parentElement && node.parentElement.closest('.mc-cr-loadmore, .mc-cr-pager, .mc-cr-ghost')); };
    new MutationObserver(function (muts) {
      if (muts.every(function (m) { return ours(m.target); })) return;
      clearTimeout(pending);
      pending = setTimeout(function () { decorate(results); }, 60);
    }).observe(results, { childList: true, subtree: true, characterData: true });
  }

  pollerLite(['.collection-main--wrapper .collection-title', '#prefixbox-search-container'], initPage, 8000);
  // phase B starts on the app's first render — cards, or its zero-match message when the URL already filters everything out
  pollerLite([function () { return !!qs('#pfbx-results-container .prefixbox-product-container, #pfbx-results-container .prefixbox-no-result-text'); }], initGrid, 20000);
})();
